// Inquiries panel (desktop): new contact and quote requests from the connected website(s).
// Data: GET /api/platform/inquiries?status=new (local; read with the person's own Xelent Sites
// connection). "Draft reply with agent" opens a new chat with the server-built prompt; the agent
// reads the inquiry, drafts, and asks before anything is sent.
import { useEffect, useState } from "preact/hooks";

type Inquiry = {
  id: string;
  kind: string;
  source: string;
  receivedAt: string;
  status: "new" | "replied" | "closed";
  contact: { name: string; email: string | null; phone: string | null; company: string | null; country: string | null };
  subject: string | null;
  excerpt: string;
  quantity: number | null;
  lastReplyAt: string | null;
  openDrafts: number;
  canReplyByEmail: boolean;
  draftPrompt: string;
};
type SiteInquiries = { siteId: string; siteName: string; inquiries: Inquiry[]; counts: Record<string, number>; error?: string };
type Data = { connected: boolean; available: boolean; sites: SiteInquiries[]; error?: string };
type Status = "new" | "replied" | "closed" | "all";

async function getJson<T>(path: string): Promise<T> {
  const r = await fetch(`/api${path}`);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j as T;
}

/** Number of new inquiries across the connected websites, for the sidebar badge; refreshed every 5 minutes. */
export function useNewInquiryCount(desktop: boolean): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!desktop) return;
    const load = () => getJson<Data>("/platform/inquiries?status=new")
      .then((d) => setCount(d.sites.reduce((sum, s) => sum + (s.counts.new ?? s.inquiries.length), 0)))
      .catch(() => setCount(0));
    load();
    const t = setInterval(load, 300_000);
    return () => clearInterval(t);
  }, [desktop]);
  return count;
}

const CSS = `
.inq__bar{display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin:-4px 0 16px}
.inq__site{margin:0 0 22px}
.inq__site h3{margin:0 0 8px;font-size:15px}
.inq__list{display:flex;flex-direction:column;gap:8px}
.inq__item{background:var(--bg2);border:1px solid var(--line);border-radius:12px;padding:12px 14px;display:grid;grid-template-columns:1fr auto;gap:6px 14px;align-items:start}
.inq__who{font-weight:600}.inq__who span{color:var(--muted);font-weight:400;font-size:12.5px;margin-left:6px}
.inq__subj{font-size:13.5px}
.inq__ex{color:var(--muted);font-size:13px;grid-column:1/2;white-space:pre-wrap;overflow-wrap:anywhere}
.inq__meta{color:var(--muted);font-size:12px;grid-column:1/2}
.inq__act{grid-row:1/4;grid-column:2/3;display:flex;flex-direction:column;gap:6px;align-items:flex-end}
.inq__tag{font-size:11px;border:1px solid var(--line);border-radius:99px;padding:1px 8px;color:var(--muted)}
@media (max-width:640px){.inq__item{grid-template-columns:1fr}.inq__act{grid-row:auto;grid-column:1/2;flex-direction:row;align-items:center}}
`;

const when = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

export function InquiriesPanel({ onDraft }: { onDraft: (prompt: string) => void }) {
  const [status, setStatus] = useState<Status>("new");
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async (s = status) => {
    setBusy(true);
    setErr("");
    try {
      setData(await getJson<Data>(`/platform/inquiries?status=${s}`));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load(status);
  }, [status]);

  const total = data?.sites.reduce((sum, s) => sum + s.inquiries.length, 0) ?? 0;
  return (
    <div class="admin" style="max-width:900px">
      <style>{CSS}</style>
      <h2>Inquiries</h2>
      <p class="hint" style="margin:-6px 0 14px">Contact and quote requests buyers sent through your website. Click "Draft reply with agent": the agent reads the request, checks your website for the facts and shows you a reply. Nothing is sent until you say yes.</p>
      <div class="inq__bar">
        {(["new", "replied", "closed", "all"] as Status[]).map((s) => (
          <button class={`btn small${status === s ? " primary" : ""}`} onClick={() => setStatus(s)}>{s === "new" ? "Needs a reply" : s[0]!.toUpperCase() + s.slice(1)}</button>
        ))}
        <button class="btn small" disabled={busy} onClick={() => load()}>{busy ? "Loading…" : "Refresh"}</button>
      </div>
      {err && <div class="hint" style="color:var(--danger, #f87171)">{err}</div>}
      {data && !data.connected && <div class="hint">Connect Xelent Sites first (the "Xelent Sites" button in the sidebar).</div>}
      {data?.connected && !data.available && <div class="hint">Your Xelent Sites connection does not offer inquiries yet. Open "Xelent Sites" and press Refresh, or try again later.</div>}
      {data?.available && total === 0 && !data.sites.some((s) => s.error) && <div class="hint">{status === "new" ? "No inquiries waiting for a reply." : "Nothing here."}</div>}
      {data?.sites.map((site) => (site.inquiries.length || site.error) ? (
        <div class="inq__site">
          {data.sites.length > 1 && <h3>{site.siteName} <span class="hint">({site.inquiries.length})</span></h3>}
          {site.error && <div class="hint">Could not read {site.siteName}: {site.error}</div>}
          <div class="inq__list">
            {site.inquiries.map((q) => (
              <div class="inq__item">
                <div class="inq__who">{q.contact.name || "Buyer"}<span>{[q.contact.company, q.contact.country].filter(Boolean).join(" · ")}</span></div>
                <div class="inq__act">
                  <button class="btn small primary" onClick={() => onDraft(q.draftPrompt)}>Draft reply with agent</button>
                  <span class="inq__tag">{q.status}{q.openDrafts ? " · draft saved" : ""}</span>
                </div>
                {q.subject && <div class="inq__subj">{q.subject}</div>}
                <div class="inq__ex">{q.excerpt}</div>
                <div class="inq__meta">
                  {when(q.receivedAt)} · {q.kind} · {q.contact.email ?? q.contact.phone ?? "no contact"}{q.quantity ? ` · qty ${q.quantity}` : ""}
                  {!q.canReplyByEmail && " · phone only (the agent gives you a WhatsApp link)"}
                  {q.lastReplyAt && ` · replied ${when(q.lastReplyAt)}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null)}
    </div>
  );
}
